import React, { FC, useContext, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import Routes from './routes/Routes';
import { ThemeContext } from './contexts/ThemeContext';
import { useCurrentUser, UserProvider } from './contexts/UserContext';
import { ThemeColorContext } from './contexts/ThemeColorContext';
import './App.css';
import { useViewerStore } from './store/store';
import { UnitContext } from './contexts/UnitContext';
import rotateScreenImage from './assets/img/i-pad/ipad-portrait-view-image.png';
import { useNavigate } from 'react-router-dom';
import { CustomNavigationClient } from './utils/NavigationClient';
import { MsalProvider } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';


interface AppProps {
    pca: PublicClientApplication;
}

const App: FC<AppProps> = ({ pca }) => {
    const navigate = useNavigate();

    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    return (
        <MsalProvider instance={pca}>
            <UserProvider>
                <Pages />
            </UserProvider>
        </MsalProvider>
    );
};



const Pages: FC = () => {
    const { theme } = useContext(ThemeContext);
    const { user } = useCurrentUser();
    const { primary } = useContext(ThemeColorContext);
    const unitContext = useContext(UnitContext);

    const isIframeEmbeddedInOneButton = useViewerStore(
        (s) => s.isIframeEmbeddedInOneButton,
    );


    useEffect(() => {
        useViewerStore.setState({unitContext : unitContext})
    }, [unitContext]);

    useEffect(() => {
        const portrait = window.matchMedia('(orientation: portrait)');
        const alertEl = document.getElementById('portraitAlertScreen');

        const applyStyles = (isPortrait: boolean) => {
            if (!alertEl) return;
            if (isPortrait) {
                alertEl.style.display = 'flex';
            } else {
                alertEl.style.display = 'none';
                useViewerStore.setState({ isRightSidebarOpen: true });
            }
        };

        applyStyles(portrait.matches);

        const listener = (e: MediaQueryListEvent) => applyStyles(e.matches);
        portrait.addEventListener('change', listener);

        return () => portrait.removeEventListener('change', listener);
    }, []);

    return (
        <>
            <div id='portraitAlertScreen' className='portraitAlertScreen'>
                <img height={200} src={rotateScreenImage} alt='rotate screen' />
                <h4>Please rotate your device to landscape mode to use this application</h4>
            </div>

            <div
                className='app'
                data-isiframeembeddedinonebutton={isIframeEmbeddedInOneButton}
                data-theme={theme}
                data-user={JSON.stringify(user)}
            >
                <ConfigProvider
                    theme={{
                        token: {
                            colorBgBase: theme === 'dark' ? '#2c2d33' : '#ffffff',
                            colorPrimary: primary,
                            colorPrimaryBg: theme === 'dark' ? 'rgba(0,0,0,0.1)' : '#ffffff',
                            colorTextBase: theme === 'dark' ? '#ffffff' : '#000000',
                            borderRadius: 6,
                        },
                    }}
                >
                    <Routes />
                </ConfigProvider>
            </div>
        </>
    );
};

export default App;
